// The artifact as the attester will see it, prepared on the device before anything leaves it:
// an image is downscaled (longest side 1600 px) and re-encoded as JPEG, which also drops its
// metadata (EXIF, GPS); a PDF is kept as is. The SHA-256 is of these prepared bytes, so the
// hash in the signed claim is the hash of exactly what the attester decrypts.
import { MAX_ARTIFACT_BYTES, sha256Hex } from "./crypto";

export const MAX_SIDE = 1600;
export const ACCEPT = "image/*,application/pdf";

export interface Prepared { bytes: Uint8Array; mime: "image/jpeg" | "application/pdf"; hash: string; downscaled: boolean;
                            width?: number; height?: number; originalBytes: number }

export async function prepareArtifact(file: File): Promise<Prepared> {
  if (file.size > MAX_ARTIFACT_BYTES) throw new Error("The file is over 10 MB. Pick a smaller one.");
  if (file.type === "application/pdf") {
    const bytes = new Uint8Array(await file.arrayBuffer());
    return { bytes, mime: "application/pdf", hash: await sha256Hex(bytes), downscaled: false, originalBytes: file.size };
  }
  if (!file.type.startsWith("image/")) throw new Error("Attach a photo or a PDF.");
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale)), h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d")!.drawImage(bmp, 0, 0, w, h);
  bmp.close?.();
  const blob = await new Promise<Blob>((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("could not encode the image"))), "image/jpeg", 0.85));
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return { bytes, mime: "image/jpeg", hash: await sha256Hex(bytes), downscaled: scale < 1, width: w, height: h, originalBytes: file.size };
}
